/* Emboro theme — vanilla JS, no dependencies */
(() => {
  'use strict';

  const T = window.theme || {};
  const routes = T.routes || {};
  const strings = T.strings || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = (routes.root || '/').replace(/\/?$/, '/');
  const url = (p) => root + p.replace(/^\//, '');

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    format = format || T.moneyFormat || '${{amount}}';
    const fmt = (n, dec, thou = ',', decSep = '.') => {
      const fixed = (n / 100).toFixed(dec);
      const [i, d] = fixed.split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (d ? decSep + d : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return fmt(cents, 2);
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return fmt(cents, 0, ' ', ',');
        default: return fmt(cents, 2);
      }
    });
  }

  /* ---------- Helpers ---------- */
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  let toastTimer;
  function toast(msg, isError) {
    const el = $('#Toast');
    if (!el) return;
    el.textContent = msg;
    el.classList.toggle('is-error', !!isError);
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2800);
  }

  let lockCount = 0;
  const lock = () => { lockCount++; document.body.classList.add('is-locked'); };
  const unlock = () => { lockCount = Math.max(0, lockCount - 1); if (!lockCount) document.body.classList.remove('is-locked'); };

  /* Generic overlay (drawer / modal) open/close with focus return */
  const openStack = [];
  function openLayer(el, focusEl) {
    if (!el || el.classList.contains('is-open')) return;
    el._returnFocus = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    lock();
    openStack.push(el);
    setTimeout(() => {
      const f = focusEl || el.querySelector('[autofocus], input[type="search"], button, a[href], input, select, textarea');
      if (f) f.focus({ preventScroll: true });
    }, 60);
  }
  function closeLayer(el) {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    unlock();
    const i = openStack.indexOf(el);
    if (i > -1) openStack.splice(i, 1);
    if (el._returnFocus && el._returnFocus.focus) el._returnFocus.focus({ preventScroll: true });
  }
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openStack.length) closeLayer(openStack[openStack.length - 1]);
  });
  document.addEventListener('click', (e) => {
    const c = e.target.closest('[data-close]');
    if (c) {
      const layer = c.closest('.drawer, .modal');
      if (layer) { e.preventDefault(); closeLayer(layer); }
    }
  });

  /* ---------- Reveal on scroll ---------- */
  function initReveal(scope = document) {
    const els = $$('.js-reveal:not(.is-in)', scope);
    if (!document.body.classList.contains('reveal-on') || reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Header ---------- */
  function initHeader() {
    const wrap = $('[data-header-wrap]');
    if (!wrap) return;
    const mode = wrap.dataset.sticky || 'always';
    const setH = () => document.documentElement.style.setProperty('--header-sticky-h', mode === 'none' ? '0px' : wrap.offsetHeight + 'px');
    setH();
    window.addEventListener('resize', debounce(setH, 150));
    let last = window.scrollY;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      wrap.classList.toggle('is-scrolled', y > 10);
      if (mode === 'scroll-up') {
        const hide = y > last && y > 300 && !openStack.length;
        wrap.classList.toggle('is-hidden', hide);
      }
      last = y;
    }, { passive: true });

    // Dropdown keyboard / click toggles (desktop)
    $$('[data-dropdown-toggle]', wrap).forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const item = btn.closest('.nav__item');
        const open = !item.classList.contains('is-open');
        $$('.nav__item.is-open', wrap).forEach((i) => { i.classList.remove('is-open'); const b = i.querySelector('[data-dropdown-toggle]'); b && b.setAttribute('aria-expanded', 'false'); });
        item.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', String(open));
        e.stopPropagation();
      });
    });
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.nav__item')) $$('.nav__item.is-open').forEach((i) => i.classList.remove('is-open'));
    });
  }
  document.addEventListener('click', (e) => {
    const m = e.target.closest('[data-menu-open]');
    if (m) { e.preventDefault(); openLayer($('#MenuDrawer')); }
    const s = e.target.closest('[data-search-open]');
    if (s) { e.preventDefault(); openLayer($('#SearchModal'), $('#SearchModal [data-predictive-input]')); }
    const c = e.target.closest('[data-cart-open]');
    if (c && $('#CartDrawer') && T.cartType !== 'page' && !document.body.classList.contains('template-cart')) { e.preventDefault(); openLayer($('#CartDrawer')); }
  });

  /* ---------- Announcement ---------- */
  function initAnnouncement(scope = document) {
    $$('[data-announcement]', scope).forEach((bar) => {
      if (bar._init) return; bar._init = true;
      const msgs = $$('.announcement__msg', bar);
      if (msgs.length < 2) return;
      let i = 0; let timer;
      const go = (n) => { msgs[i].classList.remove('is-active'); i = (n + msgs.length) % msgs.length; msgs[i].classList.add('is-active'); };
      const speed = (parseInt(bar.dataset.speed, 10) || 5) * 1000;
      const start = () => { stop(); if (bar.dataset.autoplay !== 'false') timer = setInterval(() => go(i + 1), speed); };
      const stop = () => clearInterval(timer);
      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', start);
      const p = $('[data-ann-prev]', bar), n = $('[data-ann-next]', bar);
      p && p.addEventListener('click', () => { go(i - 1); start(); });
      n && n.addEventListener('click', () => { go(i + 1); start(); });
      start();
    });
  }

  /* ---------- Hero slider ---------- */
  function initHero(scope = document) {
    $$('[data-hero]', scope).forEach((hero) => {
      if (hero._hero) hero._hero.destroy();
      const track = $('[data-hero-track]', hero);
      const slides = $$('[data-hero-slide]', hero);
      const dots = $$('[data-hero-dot]', hero);
      const fade = hero.classList.contains('hero--fade');
      const autoplay = hero.dataset.autoplay === 'true' && slides.length > 1 && !reduceMotion;
      const delay = (parseInt(hero.dataset.speed, 10) || 5) * 1000;
      let index = 0; let timer = null; let paused = false;

      const setActive = (n) => {
        index = n;
        slides.forEach((s, k) => {
          const on = k === n;
          s.classList.toggle('is-active', on);
          s.setAttribute('aria-hidden', String(!on));
          $$('a, button', s).forEach((f) => { f.tabIndex = on ? 0 : -1; });
        });
        dots.forEach((d, k) => { d.classList.toggle('is-active', k === n); d.setAttribute('aria-current', k === n ? 'true' : 'false'); });
      };
      const go = (n, user) => {
        n = (n + slides.length) % slides.length;
        if (fade) setActive(n);
        else track.scrollTo({ left: slides[n].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
        if (user) restart();
      };
      const tick = () => { if (!paused && !document.hidden) go(index + 1); };
      const restart = () => { clearInterval(timer); if (autoplay) timer = setInterval(tick, delay); };

      if (!fade) {
        let raf;
        track.addEventListener('scroll', () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            const n = Math.round(track.scrollLeft / track.clientWidth);
            if (n !== index && slides[n]) setActive(n);
          });
        }, { passive: true });
      } else {
        // swipe support for fade mode
        let x0 = null;
        track.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
        track.addEventListener('touchend', (e) => {
          if (x0 === null) return;
          const dx = e.changedTouches[0].clientX - x0;
          if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1), true);
          x0 = null;
        });
      }

      const prev = $('[data-hero-prev]', hero), next = $('[data-hero-next]', hero);
      prev && prev.addEventListener('click', () => go(index - 1, true));
      next && next.addEventListener('click', () => go(index + 1, true));
      dots.forEach((d, k) => d.addEventListener('click', () => go(k, true)));
      hero.addEventListener('mouseenter', () => { paused = true; });
      hero.addEventListener('mouseleave', () => { paused = false; });
      hero.addEventListener('focusin', () => { paused = true; });
      hero.addEventListener('focusout', () => { paused = false; });
      hero.addEventListener('touchstart', () => { paused = true; }, { passive: true });
      hero.addEventListener('touchend', () => { setTimeout(() => { paused = false; restart(); }, 1500); }, { passive: true });
      hero.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') go(index - 1, true);
        if (e.key === 'ArrowRight') go(index + 1, true);
      });

      setActive(0);
      restart();
      hero._hero = {
        destroy: () => clearInterval(timer),
        goTo: (n) => { paused = true; go(n); }
      };
    });
  }

  /* ---------- Card image sliders ---------- */
  function initCards(scope = document) {
    $$('[data-card]', scope).forEach((card) => {
      if (card._init) return; card._init = true;
      const track = $('[data-card-track]', card);
      if (!track) return;
      const slides = $$('.card__slide', track);
      if (slides.length < 2) { slides[0] && slides[0].classList.add('is-current'); return; }
      const dots = $$('.card__dot', card);
      const prev = $('[data-card-prev]', card), next = $('[data-card-next]', card);
      let idx = 0;
      const update = () => {
        const n = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        idx = Math.max(0, Math.min(slides.length - 1, n));
        slides.forEach((s, k) => { s.classList.toggle('is-current', k === idx); s.setAttribute('aria-hidden', String(k !== idx)); });
        dots.forEach((d, k) => d.classList.toggle('is-active', k === idx));
        if (prev) prev.disabled = idx === 0;
        if (next) next.disabled = idx === slides.length - 1;
      };
      let raf;
      track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
      const go = (n, e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        n = Math.max(0, Math.min(slides.length - 1, n));
        track.scrollTo({ left: n * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      prev && prev.addEventListener('click', (e) => go(idx - 1, e));
      next && next.addEventListener('click', (e) => go(idx + 1, e));
      // Preload the 2nd image on first hover for instant arrow response
      card.addEventListener('mouseenter', () => {
        $$('img[loading="lazy"]', track).slice(0, 2).forEach((img) => { img.loading = 'eager'; });
      }, { once: true });
      update();
    });
  }

  /* ---------- Horizontal rails (product carousels) ---------- */
  function initRails(scope = document) {
    $$('[data-rail]', scope).forEach((rail) => {
      if (rail._init) return; rail._init = true;
      const track = $('[data-rail-track]', rail);
      const section = rail.closest('.section') || rail;
      const prev = $('[data-rail-prev]', section), next = $('[data-rail-next]', section);
      if (!track) return;
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      };
      const step = () => {
        const item = track.firstElementChild;
        return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth;
      };
      prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  }

  /* ---------- Product gallery ---------- */
  function initGalleries(scope = document) {
    $$('[data-gallery]', scope).forEach((g) => {
      if (g._init) return;
      const track = $('[data-gallery-track]', g);
      const slides = $$('.gallery__slide', track);
      const thumbs = $$('[data-thumb]', g);
      const counter = $('[data-gallery-index]', g);
      let idx = 0;
      const update = () => {
        idx = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
        thumbs.forEach((t, k) => t.classList.toggle('is-active', k === idx));
        if (counter) counter.textContent = idx + 1;
        const at = thumbs[idx];
        if (at && at.parentElement) {
          const p = at.parentElement;
          const left = at.offsetLeft - p.clientWidth / 2 + at.clientWidth / 2;
          p.scrollTo({ left, behavior: 'smooth' });
        }
      };
      const go = (n, instant) => {
        n = (n + slides.length) % slides.length;
        track.scrollTo({ left: n * track.clientWidth, behavior: instant || reduceMotion ? 'auto' : 'smooth' });
      };
      let raf;
      track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
      thumbs.forEach((t) => t.addEventListener('click', () => go(parseInt(t.dataset.thumb, 10))));
      const p = $('[data-gallery-prev]', g), n = $('[data-gallery-next]', g);
      p && p.addEventListener('click', () => go(idx - 1));
      n && n.addEventListener('click', () => go(idx + 1));

      // hover zoom (desktop)
      if (window.matchMedia('(hover: hover) and (min-width: 990px)').matches) {
        $$('.gallery__slide.is-zoomable', g).forEach((s) => {
          const img = $('img', s);
          if (!img) return;
          s.addEventListener('mouseenter', () => s.classList.add('is-zoomed'));
          s.addEventListener('mouseleave', () => { s.classList.remove('is-zoomed'); img.style.removeProperty('--zx'); img.style.removeProperty('--zy'); });
          s.addEventListener('mousemove', (e) => {
            const r = s.getBoundingClientRect();
            img.style.setProperty('--zx', ((e.clientX - r.left) / r.width * 100) + '%');
            img.style.setProperty('--zy', ((e.clientY - r.top) / r.height * 100) + '%');
          });
        });
      }

      g._goToMedia = (mediaId) => {
        const k = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
        if (k > -1) go(k);
      };
      const featured = slides.findIndex((s) => s.hasAttribute('data-featured'));
      if (featured > 0) requestAnimationFrame(() => go(featured, true));
      g._init = true;
      update();
    });
  }

  /* ---------- Quantity steppers (product forms) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
    if (!btn) return;
    const wrap = btn.closest('[data-qty]');
    const input = wrap && $('input', wrap);
    if (!input) return;
    const min = parseInt(input.min, 10) || 0;
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    let v = parseInt(input.value, 10) || 0;
    v = btn.hasAttribute('data-qty-plus') ? Math.min(max, v + 1) : Math.max(min, v - 1);
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Variant picker / buy box ---------- */
  function initBuyBoxes(scope = document) {
    $$('[data-buy-box]', scope).forEach((box) => {
      if (box._init) return; box._init = true;
      const jsonEl = $('[data-product-json]', box);
      if (!jsonEl) return;
      let data;
      try { data = JSON.parse(jsonEl.textContent); } catch (err) { return; }
      const picker = $('[data-variant-picker]', box);
      const idInput = $('[data-variant-id]', box);
      const addBtn = $('[data-add-btn]', box);
      const addLabel = $('[data-add-label]', box);
      const buyNow = $('[data-buy-now]', box);
      const updateUrl = box.hasAttribute('data-update-url');
      const scopeEl = box.closest('[data-product-scope]') || box.parentElement;
      if (!picker) return;

      const selected = () => {
        const groups = $$('.variant-picker__group', picker);
        return groups.map((g) => { const c = $('input:checked', g); return c ? c.value : null; });
      };
      const refreshAvailability = (opts) => {
        // mark values that have no available variant given the other selected options
        $$('.variant-picker__group', picker).forEach((g, gi) => {
          $$('input', g).forEach((inp) => {
            const ok = data.variants.some((v) => v.available && v.options[gi] === inp.value && v.options.slice(0, gi).every((o, k) => o === opts[k]));
            const label = inp.nextElementSibling;
            if (label) label.classList.toggle('is-unavailable', !ok);
          });
        });
      };
      const update = () => {
        const opts = selected();
        const v = data.variants.find((x) => x.options.every((o, k) => o === opts[k]));
        opts.forEach((o, k) => { const lbl = $(`[data-option-value="${k}"]`, box); if (lbl) lbl.textContent = o; });
        refreshAvailability(opts);
        if (!v) {
          if (addBtn) addBtn.disabled = true;
          if (addLabel) addLabel.textContent = strings.unavailable || 'Unavailable';
          if (buyNow) buyNow.disabled = true;
          return;
        }
        idInput.value = v.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
        // price
        const priceWrap = $('[data-price]', box);
        if (priceWrap) {
          const onSale = v.compare_at_price > v.price;
          priceWrap.classList.toggle('price--sale', onSale);
          const cur = $('[data-price-current]', priceWrap); if (cur) cur.textContent = formatMoney(v.price);
          const cmp = $('[data-price-compare]', priceWrap);
          if (cmp) { cmp.hidden = !onSale; cmp.textContent = onSale ? formatMoney(v.compare_at_price) : ''; }
          const save = $('[data-price-save]', priceWrap);
          if (save) { save.hidden = !onSale; save.textContent = onSale ? '-' + Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price) + '%' : ''; }
          const from = $('.price__from', priceWrap); if (from) from.remove();
        }
        if (addBtn) addBtn.disabled = !v.available;
        if (addLabel) addLabel.textContent = v.available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Sold out');
        if (buyNow) buyNow.disabled = !v.available;
        const sku = $('[data-sku]', box); if (sku) { sku.hidden = !v.sku; const s = $('span', sku); if (s) s.textContent = v.sku || ''; }
        const stock = $('[data-stock]', box);
        if (stock) stock.innerHTML = v.available ? `<span class="dot dot--ok"></span>${box.dataset.inStock || 'In stock, ready to ship'}` : `<span class="dot dot--no"></span>${strings.soldOut || 'Sold out'}`;
        if (v.media_id) {
          const g = $('[data-gallery]', scopeEl);
          if (g && g._goToMedia) g._goToMedia(v.media_id);
        }
        if (updateUrl && window.history.replaceState) {
          const u = new URL(window.location.href);
          u.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', u.toString());
        }
        // sticky ATC sync
        const sticky = $('[data-sticky-atc]');
        if (sticky && updateUrl) {
          const sp = $('[data-sticky-price]', sticky); if (sp) sp.textContent = formatMoney(v.price);
          const sb = $('[data-sticky-btn]', sticky); if (sb) sb.disabled = !v.available;
        }
      };
      picker.addEventListener('change', update);
      refreshAvailability(selected());
    });
  }

  /* ---------- Cart ---------- */
  const CART_SECTION = 'cart-drawer';
  function cartSections() {
    const ids = [CART_SECTION];
    const page = $('[data-cart-page]');
    if (page && page.dataset.sectionId) ids.push(page.dataset.sectionId);
    return ids;
  }
  function renderCartSections(sections) {
    if (!sections) return;
    const drawerHTML = sections[CART_SECTION];
    if (drawerHTML) {
      const doc = parseHTML(drawerHTML);
      const fresh = $('[data-cart-drawer-inner]', doc);
      const cur = $('[data-cart-drawer-inner]');
      if (fresh && cur) cur.replaceWith(fresh);
      const count = fresh ? parseInt(fresh.dataset.itemCount, 10) : null;
      if (count !== null && !isNaN(count)) setCount(count);
    }
    const page = $('[data-cart-page]');
    if (page && sections[page.dataset.sectionId]) {
      const doc = parseHTML(sections[page.dataset.sectionId]);
      const fresh = $('[data-cart-page]', doc);
      if (fresh) page.replaceWith(fresh);
    }
  }
  function setCount(n) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = n;
      el.hidden = n < 1;
      el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump');
    });
  }
  async function refreshCart() {
    try {
      const res = await fetch(`${url('')}?sections=${cartSections().join(',')}`);
      renderCartSections(await res.json());
    } catch (e) { /* ignore */ }
  }

  async function addToCart(form, submitter) {
    const btn = submitter || $('[data-add-btn]', form);
    const errEl = $('[data-form-error]', form);
    if (errEl) errEl.hidden = true;
    btn && btn.classList.add('is-loading');
    const fd = new FormData(form);
    if (!fd.get('quantity')) fd.set('quantity', '1');
    fd.append('sections', cartSections().join(','));
    fd.append('sections_url', window.location.pathname);
    try {
      const res = await fetch(url('cart/add.js'), { method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' } });
      const data = await res.json();
      if (!res.ok || data.status) {
        const msg = data.description || data.message || strings.error;
        if (errEl) { errEl.textContent = msg; errEl.hidden = false; }
        toast(msg, true);
        return false;
      }
      if (T.cartType === 'page') { window.location.href = routes.cart || url('cart'); return true; }
      renderCartSections(data.sections);
      const qv = $('#QuickView');
      if (qv && qv.classList.contains('is-open')) closeLayer(qv);
      if ($('#CartDrawer') && !document.body.classList.contains('template-cart')) openLayer($('#CartDrawer'));
      else toast(strings.added || 'Added to cart');
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      return true;
    } catch (e) {
      toast(strings.error, true);
      return false;
    } finally {
      btn && btn.classList.remove('is-loading');
    }
  }

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    addToCart(form, e.submitter && e.submitter.hasAttribute('data-add-btn') ? e.submitter : null);
  });

  // Buy now: add selected variant, then go straight to Shopify checkout
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-buy-now]');
    if (!b) return;
    e.preventDefault();
    const form = b.closest('form');
    if (!form) return;
    b.classList.add('is-loading');
    const fd = new FormData(form);
    if (!fd.get('quantity')) fd.set('quantity', '1');
    try {
      const res = await fetch(url('cart/add.js'), { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
      const data = await res.json();
      if (!res.ok || data.status) { toast(data.description || strings.error, true); b.classList.remove('is-loading'); return; }
      window.location.href = url('checkout');
    } catch (err) {
      toast(strings.error, true);
      b.classList.remove('is-loading');
    }
  });

  async function changeLine(line, quantity, lineEl) {
    lineEl && lineEl.classList.add('is-loading');
    try {
      const res = await fetch(url('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: cartSections(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        toast(data.description || data.message || strings.error, true);
        await refreshCart();
        return;
      }
      renderCartSections(data.sections);
    } catch (e) {
      toast(strings.error, true);
      lineEl && lineEl.classList.remove('is-loading');
    }
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-line-remove], [data-line-minus], [data-line-plus]');
    if (!btn) return;
    e.preventDefault();
    const lineEl = btn.closest('[data-line]');
    const line = parseInt(lineEl.dataset.line, 10);
    const input = $('[data-line-qty]', lineEl);
    let q = parseInt(input ? input.value : 1, 10) || 0;
    if (btn.hasAttribute('data-line-remove')) q = 0;
    else if (btn.hasAttribute('data-line-plus')) q += 1;
    else q = Math.max(0, q - 1);
    if (input) input.value = q;
    changeLine(line, q, lineEl);
  });
  document.addEventListener('change', debounce((e) => {
    const input = e.target.closest && e.target.closest('[data-line-qty]');
    if (!input) return;
    const lineEl = input.closest('[data-line]');
    changeLine(parseInt(lineEl.dataset.line, 10), Math.max(0, parseInt(input.value, 10) || 0), lineEl);
  }, 350));
  document.addEventListener('change', debounce((e) => {
    const note = e.target.closest && e.target.closest('[data-cart-note]');
    if (!note) return;
    fetch(url('cart/update.js'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
  }, 300));
  // keep drawer in sync when coming back via browser back button
  window.addEventListener('pageshow', (e) => { if (e.persisted) refreshCart(); });

  /* ---------- Quick view ---------- */
  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (!trigger) return;
    e.preventDefault();
    const modal = $('#QuickView');
    const content = $('[data-quick-view-content]', modal);
    content.innerHTML = '<div class="quick-view__loading"><span class="loader"></span></div>';
    openLayer(modal, $('.modal__close', modal));
    try {
      const u = new URL(trigger.dataset.quickView, window.location.href);
      u.searchParams.set('section_id', 'quick-view');
      const res = await fetch(u.toString());
      if (!res.ok) throw new Error();
      const doc = parseHTML(await res.text());
      const inner = $('.quick-view__grid', doc);
      content.innerHTML = '';
      if (inner) content.appendChild(inner);
      initScope(content);
    } catch (err) {
      window.location.href = trigger.dataset.quickView;
    }
  });

  /* ---------- Predictive search ---------- */
  function initSearch() {
    const modal = $('#SearchModal');
    if (!modal) return;
    const input = $('[data-predictive-input]', modal);
    const results = $('[data-predictive-results]', modal);
    const defaults = $('[data-predictive-default]', modal);
    if (!input || !results || results.hasAttribute('data-disabled')) return;
    let ctrl;
    const run = debounce(async () => {
      const q = input.value.trim();
      if (!q) { results.innerHTML = ''; defaults.hidden = false; return; }
      if (ctrl) ctrl.abort();
      ctrl = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,page,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag'
      });
      try {
        const res = await fetch(`${routes.predictiveSearch || url('search/suggest')}?${params}`, { signal: ctrl.signal });
        if (!res.ok) throw new Error();
        const doc = parseHTML(await res.text());
        const fresh = $('[data-ps]', doc);
        results.innerHTML = fresh ? fresh.outerHTML : '';
        defaults.hidden = true;
      } catch (err) { if (err.name !== 'AbortError') results.innerHTML = ''; }
    }, 220);
    input.addEventListener('input', run);
    input.addEventListener('keydown', (e) => {
      const items = $$('[data-ps-item]', results);
      if (!items.length || !['ArrowDown', 'ArrowUp'].includes(e.key)) return;
      e.preventDefault();
      let i = items.findIndex((x) => x.getAttribute('aria-selected') === 'true');
      items.forEach((x) => x.setAttribute('aria-selected', 'false'));
      i = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
      items[i].setAttribute('aria-selected', 'true');
      items[i].focus();
    });
  }

  /* ---------- Collection filtering ---------- */
  function initFacets() {
    const wrap = $('[data-facets-wrap]');
    if (!wrap) return;
    const sectionId = wrap.dataset.sectionId;
    const load = async (href, push = true) => {
      const grid = $('[data-coll-grid]');
      grid && grid.classList.add('is-loading');
      const u = new URL(href, window.location.origin);
      u.searchParams.set('section_id', sectionId);
      try {
        const res = await fetch(u.toString());
        const doc = parseHTML(await res.text());
        ['[data-coll-grid]', '[data-facets-form-wrap]', '[data-active-filters]', '[data-coll-count]'].forEach((sel) => {
          const fresh = $(sel, doc); const cur = $(sel);
          if (fresh && cur) cur.replaceWith(fresh);
        });
        u.searchParams.delete('section_id');
        if (push) history.pushState({ facets: true }, '', u.pathname + u.search);
        initScope($('[data-coll-grid]'));
        bindForm();
      } catch (e) { window.location.href = href; }
    };
    const formToUrl = (form) => {
      const fd = new FormData(form);
      const params = new URLSearchParams();
      for (const [k, v] of fd.entries()) if (v !== '') params.append(k, v);
      const sort = $('[data-sort]');
      if (sort && !params.has('sort_by')) params.set('sort_by', sort.value);
      return `${window.location.pathname}?${params.toString()}`;
    };
    const bindForm = () => {
      const form = $('[data-facets-form]');
      if (!form || form._bound) return;
      form._bound = true;
      form.addEventListener('change', debounce(() => load(formToUrl(form)), 400));
      form.addEventListener('submit', (e) => { e.preventDefault(); load(formToUrl(form)); });
    };
    bindForm();
    document.addEventListener('change', (e) => {
      const s = e.target.closest('[data-sort]');
      if (!s) return;
      const form = $('[data-facets-form]');
      if (form) { const hidden = $('input[name="sort_by"]', form); if (hidden) hidden.value = s.value; load(formToUrl(form)); }
      else { const u = new URL(window.location.href); u.searchParams.set('sort_by', s.value); load(u.toString()); }
    });
    document.addEventListener('click', (e) => {
      const a = e.target.closest('[data-facet-link]');
      if (!a) return;
      e.preventDefault();
      load(a.href);
    });
    window.addEventListener('popstate', (e) => { if (e.state && e.state.facets) load(window.location.href, false); });
  }
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-filters-open]')) { e.preventDefault(); openLayer($('#FacetsDrawer')); }
  });

  /* ---------- Product recommendations ---------- */
  function initRecommendations(scope = document) {
    $$('[data-recommendations]', scope).forEach(async (el) => {
      if (el._init) return; el._init = true;
      try {
        const res = await fetch(el.dataset.url);
        const doc = parseHTML(await res.text());
        const fresh = $('[data-recommendations]', doc);
        if (fresh && fresh.querySelector('[data-card]')) {
          el.innerHTML = fresh.innerHTML;
          initScope(el);
        } else {
          const sec = el.closest('.shopify-section'); if (sec) sec.hidden = true;
        }
      } catch (e) { /* ignore */ }
    });
  }

  /* ---------- Sticky add to cart (mobile PDP) ---------- */
  function initStickyATC() {
    const bar = $('[data-sticky-atc]');
    const mainBtn = $('[data-main-product] [data-add-btn]');
    if (!bar || !mainBtn || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([en]) => {
      bar.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0);
    });
    io.observe(mainBtn);
    const b = $('[data-sticky-btn]', bar);
    b && b.addEventListener('click', () => {
      const form = mainBtn.closest('form');
      if (form.querySelector('[data-variant-picker]') && !form.querySelector('[data-variant-picker] input:checked')) {
        mainBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (form.requestSubmit) form.requestSubmit(mainBtn); else mainBtn.click();
    });
  }

  /* ---------- Customer: recover password toggle ---------- */
  function initAccount() {
    const login = $('[data-login-form]'), recover = $('[data-recover-form]');
    if (!login || !recover) return;
    const show = () => {
      const r = window.location.hash === '#recover';
      recover.hidden = !r; login.hidden = r;
    };
    window.addEventListener('hashchange', show);
    show();
    // address forms
  }
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-toggle-target]');
    if (!t) return;
    e.preventDefault();
    const el = document.getElementById(t.dataset.toggleTarget);
    if (el) el.hidden = !el.hidden;
  });
  document.addEventListener('submit', (e) => {
    const f = e.target.closest('[data-confirm]');
    if (f && !window.confirm(f.dataset.confirm)) e.preventDefault();
  });
  function initAddressForms() {
    $$('[data-country-select]').forEach((sel) => {
      if (sel._init) return; sel._init = true;
      const prov = document.getElementById(sel.dataset.provinceTarget);
      const provWrap = prov && prov.closest('.field');
      const fill = () => {
        const opt = sel.options[sel.selectedIndex];
        let list = [];
        try { list = JSON.parse(opt ? opt.dataset.provinces || '[]' : '[]'); } catch (e) { list = []; }
        if (!prov) return;
        const cur = prov.dataset.default || prov.value;
        prov.innerHTML = list.map(([v, l]) => `<option value="${v}"${v === cur ? ' selected' : ''}>${l}</option>`).join('');
        if (provWrap) provWrap.hidden = list.length === 0;
      };
      if (sel.dataset.default) {
        const o = Array.from(sel.options).find((x) => x.value === sel.dataset.default);
        if (o) o.selected = true;
      }
      sel.addEventListener('change', () => { if (prov) prov.dataset.default = ''; fill(); });
      fill();
    });
  }

  /* ---------- Init ---------- */
  function initScope(scope) {
    initCards(scope);
    initRails(scope);
    initGalleries(scope);
    initBuyBoxes(scope);
    initReveal(scope);
  }
  function init() {
    initHeader();
    initAnnouncement();
    initHero();
    initScope(document);
    initSearch();
    initFacets();
    initRecommendations();
    initStickyATC();
    initAccount();
    initAddressForms();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:load', (e) => {
    initAnnouncement(e.target);
    initHero(e.target);
    initScope(e.target);
    initRecommendations(e.target);
    if (e.target.querySelector('[data-header-wrap]')) initHeader();
  });
  document.addEventListener('shopify:block:select', (e) => {
    const hero = e.target.closest('[data-hero]');
    if (hero && hero._hero) {
      const slides = $$('[data-hero-slide]', hero);
      const k = slides.indexOf(e.target.closest('[data-hero-slide]') || e.target);
      if (k > -1) hero._hero.goTo(k);
    }
  });
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target.querySelector('#CartDrawer')) openLayer($('#CartDrawer'));
  });

  window.Emboro = { formatMoney, refreshCart, openLayer, closeLayer };
})();
